import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/Dialog";
import Input from "../../../../components/ui/Input";
import Button from "../../../../components/ui/Button";
import { Checkbox } from "../../../../components/ui/Checkbox";
import { CompanyBenefitTypeInput } from "../../../../models/payroll";
import { useTranslation } from "react-i18next";

type BenefitTypeFormProps = {
  open: boolean;
  onClose: () => void;
  onSave: (input: CompanyBenefitTypeInput) => void;
  benefitType?: CompanyBenefitTypeInput;
};

const BenefitTypeForm: React.FC<BenefitTypeFormProps> = ({ open, onClose, onSave, benefitType }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cost, setCost] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [visibleToEmployees, setVisibleToEmployees] = useState(false);
  const [touched, setTouched] = useState<{ [k: string]: boolean }>({});

  const errors = useMemo(() => {
    return {
      name: !name.trim() ? t("payroll:Name is required") : undefined,
      cost: cost && isNaN(Number(cost)) ? t("payroll:Invalid number") : undefined,
      price: price && isNaN(Number(price)) ? t("payroll:Invalid number") : undefined,
    };
  }, [name, cost, price, t]);

  const hasErrors = Object.values(errors).some(Boolean);

  useEffect(() => {
    if (benefitType) {
      setName(benefitType.name || "");
      setDescription(benefitType.description || "");
      setCost(benefitType.cost !== undefined && benefitType.cost !== null ? String(benefitType.cost) : "");
      setPrice(benefitType.price !== undefined && benefitType.price !== null ? String(benefitType.price) : "");
      setVisibleToEmployees(benefitType.visibleToEmployees || false);
    }
  }, [benefitType]);

  const handleSave = () => {
    setTouched({ name: true, cost: true, price: true });
    if (hasErrors) return;
    const input: CompanyBenefitTypeInput = {
      id: benefitType?.id || 0,
      name: name.trim(),
      description: description.trim() || undefined,
      cost: cost ? Number(cost) : undefined,
      price: price ? Number(price) : undefined,
      visibleToEmployees,
    };
    onSave(input);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm" data-testid="settings-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {benefitType ? t("payroll:Edit Benefit Type") : t("payroll:Add Benefit Type")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form noValidate autoComplete="off" className="flex flex-col gap-3">
            <Input
              required
              autoFocus
              id="name"
              label={t("payroll:Benefit Type Name")}
              type="text"
              value={name}
              onBlur={() => setTouched((s) => ({ ...s, name: true }))}
              error={touched.name && errors.name ? errors.name : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium text-foreground">
                {t("payroll:Description")}
              </label>
              <textarea
                id="description"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 min-h-[4.5rem]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                id="cost"
                label={t("payroll:Cost")}
                type="number"
                value={cost}
                onBlur={() => setTouched((s) => ({ ...s, cost: true }))}
                error={touched.cost && errors.cost ? errors.cost : undefined}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCost(e.target.value)}
                min={0}
              />
              <Input
                id="price"
                label={t("payroll:Price")}
                type="number"
                value={price}
                onBlur={() => setTouched((s) => ({ ...s, price: true }))}
                error={touched.price && errors.price ? errors.price : undefined}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(e.target.value)}
                min={0}
              />
            </div>
            <Checkbox
              checked={visibleToEmployees}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVisibleToEmployees(e.target.checked)}
              label={t("payroll:Visible To Employees")}
            />
            <p className="text-xs text-muted-foreground opacity-70">
              {t("payroll:Leave cost/price blank if not applicable")}
            </p>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={hasErrors} data-testid="save-btn">
            {t("common:Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BenefitTypeForm;
